/**
 * Audit dashboard helpers: the 13 audit categories folded into 5 chart groups (the categorical
 * palette validates 5 adjacent slots), plain-English action labels and heatmap levels.
 */
import { AUDIT_CATEGORY_LABELS, auditCategory, type AuditCategory } from '@1qa/shared';

export interface AuditGroup {
  key: 'access' | 'org' | 'testing' | 'reports' | 'settings';
  label: string;
  categories: AuditCategory[];
  /** CSS custom property from .viz-root (fixed slot order - never reassigned by rank). */
  color: string;
}

export const AUDIT_GROUPS: AuditGroup[] = [
  { key: 'access', label: 'Access & sign-ins', categories: ['auth', 'user', 'invitation'], color: 'var(--series-1)' },
  { key: 'org', label: 'Organizations & projects', categories: ['org', 'project'], color: 'var(--series-2)' },
  { key: 'testing', label: 'Tests, runs & test data', categories: ['test', 'run', 'variable'], color: 'var(--series-3)' },
  { key: 'reports', label: 'Reports & comments', categories: ['report', 'comment'], color: 'var(--series-4)' },
  { key: 'settings', label: 'AI models, plugins & system', categories: ['llm', 'plugin', 'system'], color: 'var(--series-5)' },
];

export function groupOf(category: AuditCategory): AuditGroup {
  return AUDIT_GROUPS.find((g) => g.categories.includes(category)) ?? AUDIT_GROUPS[AUDIT_GROUPS.length - 1]!;
}

/** Per-group totals for one day of the summary. */
export function groupTotals(byCategory: Partial<Record<AuditCategory, number>>): Record<AuditGroup['key'], number> {
  const out = { access: 0, org: 0, testing: 0, reports: 0, settings: 0 };
  for (const [cat, n] of Object.entries(byCategory) as [AuditCategory, number][]) out[groupOf(cat).key] += n;
  return out;
}

const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.login_failed': 'Failed sign-in',
  'user.updated': 'Changed a user’s role or status',
  'invitation.platform.created': 'Invited a Project Admin',
  'invitation.platform.revoked': 'Revoked a Project Admin invitation',
  'invitation.org.created': 'Invited someone to an organization',
  'invitation.accepted': 'Accepted an invitation',
  'org.created': 'Created an organization',
  'org.updated': 'Renamed an organization',
  'org.member.role_changed': 'Changed a member’s role',
  'org.member.removed': 'Removed a member',
  'project.created': 'Created a project',
  'project.updated': 'Changed project settings',
  'project.deleted': 'Deleted a project',
  'project.member.assigned': 'Assigned a member to a project',
  'project.member.removed': 'Removed a member from a project',
  'test.created': 'Created a test',
  'test.updated': 'Edited a test',
  'test.copied': 'Copied a test',
  'test.deleted': 'Deleted a test',
  'test.requirements.uploaded': 'Uploaded business documents',
  'test.requirements.analyzed': 'Had the AI model find requirements',
  'test.requirements.generated': 'Had the AI model draft tests from requirements',
  'test.requirements.saved': 'Saved tests from business documents',
  'test.requirements.deleted': 'Deleted uploaded business documents',
  'test.group.created': 'Created a test group',
  'test.group.updated': 'Edited a test group',
  'test.group.deleted': 'Deleted a test group',
  'test.flow.created': 'Created a flow',
  'test.flow.updated': 'Edited a flow',
  'test.flow.deleted': 'Deleted a flow',
  'test.trigger.created': 'Created a schedule or webhook',
  'test.trigger.updated': 'Changed a schedule or webhook',
  'test.trigger.deleted': 'Deleted a schedule or webhook',
  'test.trigger.token_rotated': 'Replaced a webhook token',
  'run.triggered': 'Started a run',
  'run.flow.triggered': 'Started a flow',
  'run.flow.cancelled': 'Cancelled a flow run',
  'run.trigger.fired': 'A schedule or webhook started a run',
  'run.cancelled': 'Cancelled a run',
  'report.shared.user': 'Shared a report with a person',
  'report.shared.public': 'Created a public report link',
  'report.share.revoked': 'Removed report access',
  'report.deleted': 'Deleted a report',
  'report.artifact.deleted': 'Deleted a screenshot or video',
  'comment.created': 'Commented on a report',
  'variable.created': 'Added test data',
  'variable.updated': 'Changed test data',
  'variable.copied': 'Copied test data',
  'variable.deleted': 'Deleted test data',
  'llm.created': 'Connected an AI model',
  'llm.updated': 'Changed an AI model',
  'llm.default': 'Changed the default AI model',
  'llm.deleted': 'Removed an AI model',
  'plugin.upserted': 'Changed a plugin',
  'plugin.deleted': 'Removed a plugin setting',
  'plugin.tested': 'Sent a test notification',
  'bootstrap.admin.created': 'Created the first Admin',
  'system.audit.exported': 'Exported the audit log',
};

export function describeAction(action: string): string {
  return ACTION_LABELS[action] ?? `${AUDIT_CATEGORY_LABELS[auditCategory(action)]}: ${action}`;
}

/** 0 (none) .. 6 (busiest) for a heatmap cell, relative to the busiest cell. */
export function heatLevel(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  return Math.max(1, Math.min(6, Math.ceil((value / max) * 6)));
}
